import React, { useEffect, useState } from 'react';
import { formatDateLong } from '../utils/formatDate';
import { useAuth, AccountOverview } from '../contexts/AuthContext';
import { MetricCard } from './MetricCards';
import LoadErrorBox from './ui/LoadErrorBox';
import AccountBar from './account/AccountBar';
import { usePageRefresh } from '../hooks/usePageRefresh';
import { useRole } from '../hooks/useRole';
import { makeMoney, CURRENCY_NOT_SET } from '../utils/currency';
import '../styles/components/OverviewView.css';

const GREETING_HOUR_LABEL = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const formatDateHeading = (): string =>
  formatDateLong(new Date());

/**
 * The pipeline value as text. Amounts in different currencies are never added
 * together: one currency shows one figure, several show one figure each, and
 * projects with no currency are counted separately.
 */
export function pipelineValueText(overview: AccountOverview): string {
  const parts = overview.pipelineValueByCurrency.map(row => makeMoney(row.currency).format(Number(row.value)));
  if (parts.length === 0) return overview.projectsMissingCurrency > 0 ? CURRENCY_NOT_SET : '—';
  return parts.join(' · ');
}

const OverviewView: React.FC = () => {
  const { user, fetchAccountOverview } = useAuth();
  const { canViewFinancials } = useRole();
  const [overview, setOverview] = useState<AccountOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchAccountOverview().then(result => {
      if (!cancelled) {
        setOverview(result);
        setLoading(false);
      }
    }).catch(err => {
      if (!cancelled) {
        setLoadError(err);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [fetchAccountOverview, attempt]);

  // The header's Refresh reads the numbers again.
  usePageRefresh(() => setAttempt(n => n + 1), loading);

  const firstName = user?.name?.split(' ')[0] ?? 'there';

  return (
    <div className="wt-container fade-in">
        <AccountBar view="overview" />
        {/* The page title ("At a glance") is the shell's header title; this is just the welcome. */}
        <div className="ov-header">
          <div>
            <p className="ov-title">{GREETING_HOUR_LABEL()}, {firstName}</p>
            <p className="ov-subtitle">{formatDateHeading()}</p>
          </div>
        </div>

        {loadError !== null ? (
          <LoadErrorBox what="your numbers" error={loadError} onRetry={() => setAttempt(n => n + 1)} />
        ) : loading ? (
          <div className="ov-loading">Loading your studio's numbers…</div>
        ) : (
          <div className="stats-grid ov-stats-grid">
            <MetricCard
              label="Active Projects"
              value={overview?.activeProjects ?? 0}
              icon="folder"
              tone="blue"
            />
            {canViewFinancials && overview && (
              <MetricCard
                label="Pipeline Value (excl. lost)"
                value={pipelineValueText(overview)}
                title="Contract value of every project that is not lost: open plus won, per currency"
                icon="dollar"
                tone="green"
              />
            )}
            <MetricCard
              label="Tasks Due (7 days)"
              value={overview?.tasksDue ?? 0}
              icon="clock"
              tone="orange"
            />
            <MetricCard
              label="Upcoming Events"
              value={overview?.upcomingEvents ?? 0}
              icon="calendar"
              tone="purple"
            />
          </div>
        )}

        {!loading && loadError === null && overview && (
          <div className="ov-section">
            <h2 className="ov-section-title">Pipeline by stage</h2>
            <div className="ov-stage-breakdown">
              {Object.entries(overview.stageBreakdown).map(([stage, count]) => (
                <div className="ov-stage-pill" key={stage}>
                  <span className="ov-stage-pill-label">{stage}</span>
                  <span className="ov-stage-pill-count">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};

export default OverviewView;
